import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { type muiTheme, theme } from "@polifonia/theme";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { Modal } from "@/components/Modal";
import { MotifIcon } from "@/components/MotifIcon";
import { SingleSelect } from "@/components/Select";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { useConversionActions } from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/api/useConversionActions";
import {
  type ConversionMapping,
  useConversionMappings,
  useCreateConversionMapping,
  useUpdateConversionMapping,
} from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/api/useConversionMappings";
import {
  isLeadConversionReady,
  useLeadConversionReadiness,
} from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/api/useLeadConversionReadiness";

const cssBody = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const cssField = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Top common ISO 4217 currencies. Server enforces full validity; UI offers a sane default set.
const COMMON_CURRENCIES = [
  "USD",
  "EUR",
  "JPY",
  "GBP",
  "TWD",
  "HKD",
  "SGD",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
  "KRW",
  "THB",
  "MYR",
  "IDR",
  "PHP",
  "VND",
  "INR",
];

// 3-letter ISO 4217 code (server is the source of truth; this is a pre-flight check)
const CURRENCY_CODE_REGEX = /^[A-Z]{3}$/;

// Google Ads "Enhanced conversions for leads" setup guide, linked from the readiness warning.
const ENHANCED_CONVERSIONS_HELP_URL =
  "https://support.google.com/google-ads/answer/13258081";

const styles = {
  readinessList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginTop: "4px",
  },
  readinessItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  readinessOk: {
    color: theme.colors.staticFgPositive,
  },
  readinessMissing: {
    color: theme.colors.staticFgNegative,
  },
  readinessNote: {
    marginTop: "8px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

type ValueMode = "none" | "fixed";

interface AddConversionMappingModalProps {
  open: boolean;
  orgUuid: string;
  /**
   * If provided, modal pre-selects this account and disables the account picker.
   * If omitted, the user picks from accounts in `useConversionActions`.
   */
  defaultAccountId?: number | undefined;
  /**
   * If provided, modal opens in edit mode (PATCH); if absent, create mode (POST).
   */
  editing?: ConversionMapping | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddConversionMappingModal: React.FC<AddConversionMappingModalProps> =
  function AddConversionMappingModal({
    open,
    orgUuid,
    defaultAccountId,
    editing,
    onClose,
    onSuccess,
  }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const isEditMode = editing !== undefined;

    const conversionActionsQuery = useConversionActions(orgUuid);
    const accountsQuery = interlude.googleAds.useListManagerAccountAccounts();

    // Account dropdown is sourced from listManagerAccountAccounts (DB-backed,
    // gives us the row id we POST to). Conversion-action options are joined
    // in via googleAdsCustomerId since the conversion-actions list is keyed by
    // the Google Ads customer id, not the DB row id.
    const accountOptions = useMemo(() => {
      const accounts = accountsQuery.data?.accounts ?? [];
      return accounts.flatMap((a) =>
        a.id === null
          ? []
          : [
              {
                label: `${a.name} (${a.googleAdsCustomerId})`,
                value: a.id,
                googleAdsCustomerId: a.googleAdsCustomerId,
              },
            ],
      );
    }, [accountsQuery.data]);

    const [accountId, setAccountId] = useState<number | null>(
      editing?.googleAdsAccountId ?? defaultAccountId ?? null,
    );

    const mappingsQuery = useConversionMappings(orgUuid, accountId ?? 0);

    const readinessQuery = useLeadConversionReadiness(orgUuid, accountId ?? 0);

    // Non-blocking pre-flight: warn when the picked account hasn't enabled the two
    // Google Ads toggles that identifier-only (email/phone) uploads require. Stay
    // silent while loading and on error (fail-open) — the check is advisory, and the
    // toggles are eventually consistent so a fresh account may briefly still read off.
    const readinessWarning: React.ReactNode = useMemo(() => {
      const readiness = readinessQuery.data;
      if (!readiness || isLeadConversionReady(readiness)) return null;
      const { acceptedCustomerDataTerms, enhancedConversionsForLeadsEnabled } =
        readiness;

      const renderItem = (label: string, enabled: boolean) => (
        <Box
          sx={{
            ...styles.readinessItem,
            ...(enabled ? styles.readinessOk : styles.readinessMissing),
          }}
        >
          <MotifIcon
            component="span"
            un-i-motif={enabled ? "circle_check" : "circle_cross"}
          />
          <Typography variant="body" color="inherit">
            {label}
          </Typography>
        </Box>
      );

      return (
        <InfoBox
          variant="warning"
          href={ENHANCED_CONVERSIONS_HELP_URL}
          title={t(
            "applications.googleAds.conversions.modal.readiness.title",
            "This account isn't ready for email/phone-matched conversions",
          )}
          description={
            <Box>
              <Typography variant="body" color="inherit">
                {t(
                  "applications.googleAds.conversions.modal.readiness.description",
                  "Conversions with a click ID still upload, but identifier-only matches are rejected until both are enabled in Google Ads:",
                )}
              </Typography>
              <Box sx={styles.readinessList}>
                {renderItem(
                  t(
                    "applications.googleAds.conversions.modal.readiness.customerDataTerms",
                    "Customer data terms",
                  ),
                  acceptedCustomerDataTerms,
                )}
                {renderItem(
                  t(
                    "applications.googleAds.conversions.modal.readiness.enhancedConversions",
                    "Enhanced conversions for leads",
                  ),
                  enhancedConversionsForLeadsEnabled,
                )}
              </Box>
              <Box sx={styles.readinessNote}>
                <Typography variant="note" color="inherit">
                  {t(
                    "applications.googleAds.conversions.modal.readiness.eventualConsistency",
                    "Changes in Google Ads can take time to take effect.",
                  )}
                </Typography>
              </Box>
            </Box>
          }
        />
      );
    }, [readinessQuery.data, t]);

    const conversionActionOptions = useMemo(() => {
      const pickedAccount = accountOptions.find((a) => a.value === accountId);
      if (!pickedAccount) return [];
      const conversionActionsAccount =
        conversionActionsQuery.data?.accounts.find(
          (a) => a.id === pickedAccount.googleAdsCustomerId,
        );
      const usedActionIds = new Set(
        (mappingsQuery.data?.conversionMappings ?? [])
          .filter(
            (m) =>
              m.status === "active" &&
              // In edit mode, keep the currently-edited mapping's action visible
              (editing === undefined || m.id !== editing.id),
          )
          .map((m) => m.conversionActionId),
      );
      return (
        conversionActionsAccount?.conversionActions
          .filter((ca) => !usedActionIds.has(ca.conversionActionId))
          .map((ca) => ({
            label: ca.name,
            value: ca.conversionActionId,
          })) ?? []
      );
    }, [
      accountOptions,
      conversionActionsQuery.data,
      accountId,
      mappingsQuery.data,
      editing,
    ]);

    const [conversionActionId, setConversionActionId] = useState<string | null>(
      editing?.conversionActionId ?? null,
    );

    // When account changes, reset the conversion action selection (avoid stale id from old account)
    useEffect(() => {
      if (isEditMode) return;
      setConversionActionId(null);
    }, [accountId, isEditMode]);

    const [valueMode, setValueMode] = useState<ValueMode>(
      editing?.conversionValue ? "fixed" : "none",
    );
    const [conversionValue, setConversionValue] = useState<string>(
      editing?.conversionValue ?? "",
    );
    const [currencyCode, setCurrencyCode] = useState<string>(
      editing?.currencyCode ?? "USD",
    );

    // Validation
    const valueError = useMemo(() => {
      if (valueMode !== "fixed") return null;
      if (conversionValue.trim() === "") {
        return t(
          "applications.googleAds.conversions.modal.error.valueRequired",
          "Value is required",
        );
      }
      const num = Number(conversionValue);
      if (!Number.isFinite(num) || num < 0) {
        return t(
          "applications.googleAds.conversions.modal.error.valueInvalid",
          "Value must be a non-negative number",
        );
      }
      return null;
    }, [valueMode, conversionValue, t]);

    const currencyError = useMemo(() => {
      if (valueMode !== "fixed") return null;
      if (currencyCode.trim() === "") {
        return t(
          "applications.googleAds.conversions.modal.error.currencyRequired",
          "Currency is required when value is set",
        );
      }
      if (!CURRENCY_CODE_REGEX.test(currencyCode)) {
        return t(
          "applications.googleAds.conversions.modal.error.currencyInvalid",
          "Currency must be a 3-letter ISO 4217 code",
        );
      }
      return null;
    }, [valueMode, currencyCode, t]);

    const isValid =
      accountId !== null &&
      conversionActionId !== null &&
      conversionActionId.length > 0 &&
      valueError === null &&
      currencyError === null;

    // Mutations — only one will be active at a time (create xor update)
    const createMutation = useCreateConversionMapping(orgUuid, accountId ?? 0, {
      onSuccess: () => {
        enqueueSnackbar(
          t(
            "applications.googleAds.conversions.modal.createSuccess",
            "Conversion created",
          ),
          { variant: "success" },
        );
        onSuccess();
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.status === 409) {
          enqueueSnackbar(
            t(
              "applications.googleAds.conversions.modal.error.duplicateConversionAction",
              "This conversion action is already mapped for this account",
            ),
            { variant: "error" },
          );
          return;
        }
        enqueueSnackbar(
          t(
            "applications.googleAds.conversions.modal.createError",
            "Failed to create conversion",
          ),
          { variant: "error" },
        );
      },
    });

    const updateMutation = useUpdateConversionMapping(
      orgUuid,
      editing?.googleAdsAccountId ?? 0,
      editing?.id ?? 0,
      {
        onSuccess: () => {
          enqueueSnackbar(
            t(
              "applications.googleAds.conversions.modal.updateSuccess",
              "Conversion updated",
            ),
            { variant: "success" },
          );
          onSuccess();
        },
        onError: (error) => {
          if (error instanceof AxiosError && error.response?.status === 409) {
            enqueueSnackbar(
              t(
                "applications.googleAds.conversions.modal.error.duplicateConversionAction",
                "This conversion action is already mapped for this account",
              ),
              { variant: "error" },
            );
            return;
          }
          enqueueSnackbar(
            t(
              "applications.googleAds.conversions.modal.updateError",
              "Failed to update conversion",
            ),
            { variant: "error" },
          );
        },
      },
    );

    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    const handleSubmit = useHandler(() => {
      if (!isValid || accountId === null || conversionActionId === null) {
        return;
      }
      const value = valueMode === "fixed" ? conversionValue : null;
      const currency = valueMode === "fixed" ? currencyCode : null;

      if (isEditMode && editing) {
        updateMutation.mutate({
          conversionActionId,
          conversionValue: value,
          currencyCode: currency,
        });
      } else {
        createMutation.mutate({
          status: "active",
          conversionActionId,
          conversionValue: value,
          currencyCode: currency,
        });
      }
    });

    return (
      <Modal
        open={open}
        handleClose={onClose}
        title={
          isEditMode
            ? t(
                "applications.googleAds.conversions.modal.editTitle",
                "Edit Conversion",
              )
            : t(
                "applications.googleAds.conversions.modal.addTitle",
                "Add Conversion",
              )
        }
        width={520}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              {isEditMode ? t("common.save") : t("common.add")}
            </Button>
          </Stack>
        }
      >
        <div css={cssBody}>
          {/* Step 1: account */}
          <div css={cssField}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgTitle}
            >
              {t(
                "applications.googleAds.conversions.modal.accountLabel",
                "Ad account",
              )}
            </Typography>
            <SingleSelect<number | null>
              value={accountId}
              options={accountOptions}
              disabled={
                isEditMode ||
                defaultAccountId !== undefined ||
                conversionActionsQuery.isPending
              }
              onChange={(v) => setAccountId(v ?? null)}
              placeholder={t(
                "applications.googleAds.conversions.modal.accountPlaceholder",
                "Select an ad account",
              )}
            />
          </div>

          {readinessWarning}

          {/* Step 2: conversion action (cascading) */}
          <div css={cssField}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgTitle}
            >
              {t(
                "applications.googleAds.conversions.modal.conversionActionLabel",
                "Conversion action",
              )}
            </Typography>
            <SingleSelect<string | null>
              value={conversionActionId}
              options={conversionActionOptions}
              disabled={accountId === null || conversionActionsQuery.isPending}
              onChange={(v) => setConversionActionId(v ?? null)}
              placeholder={t(
                "applications.googleAds.conversions.modal.conversionActionPlaceholder",
                "Select a conversion action",
              )}
            />
          </div>

          {/* Step 3: value mode */}
          <div css={cssField}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgTitle}
            >
              {t(
                "applications.googleAds.conversions.modal.valueModeLabel",
                "Conversion value",
              )}
            </Typography>
            <RadioGroup
              value={valueMode}
              onChange={(_, v) => setValueMode(v as ValueMode)}
            >
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label={t(
                  "applications.googleAds.conversions.modal.valueModeNone",
                  "No value",
                )}
              />
              <FormControlLabel
                value="fixed"
                control={<Radio size="small" />}
                label={t(
                  "applications.googleAds.conversions.modal.valueModeFixed",
                  "Fixed value",
                )}
              />
            </RadioGroup>
          </div>

          {valueMode === "fixed" && (
            <Stack direction="row" gap="12px">
              <div css={cssField} style={{ flex: 1 }}>
                <Typography variant="note" color={theme.colors.staticFgNote}>
                  {t(
                    "applications.googleAds.conversions.modal.valueLabel",
                    "Value",
                  )}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: "0.0001" }}
                  value={conversionValue}
                  onChange={(e) => setConversionValue(e.target.value)}
                  error={valueError !== null}
                  helperText={valueError ?? undefined}
                />
              </div>
              <div css={cssField} style={{ width: 160 }}>
                <Typography variant="note" color={theme.colors.staticFgNote}>
                  {t(
                    "applications.googleAds.conversions.modal.currencyLabel",
                    "Currency",
                  )}
                </Typography>
                <SingleSelect<string>
                  value={currencyCode}
                  options={COMMON_CURRENCIES.map((c) => ({
                    label: c,
                    value: c,
                  }))}
                  onChange={(v) => setCurrencyCode(v ?? "")}
                  error={currencyError !== null}
                  helperText={currencyError ?? undefined}
                  placeholder="USD"
                />
              </div>
            </Stack>
          )}
        </div>
      </Modal>
    );
  };
